import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, AlertTriangle, Shield, MessageSquare, Copy, Check, Heart, Users, Flag, Ear, HandHeart, Ban } from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const P = SECTIONS.sops;
const TEAL = '#0D9488';
const AMBER = '#D97706';
const RED = '#DC2626';

const LEVELS = [
  {
    n: 1,
    tone: '#0D9488',
    label: 'Distress / Frustration',
    signs: ['Raised voice', 'Irritability', 'Anxiety', 'Complaints'],
    action: 'Continue care with reassurance',
  },
  {
    n: 2,
    tone: AMBER,
    label: 'Escalating Behaviour',
    signs: ['Shouting', 'Verbal aggression', 'Refusal to cooperate'],
    action: 'Senior staff involvement (senior reception · radiographer · radiologist)',
  },
  {
    n: 3,
    tone: RED,
    label: 'Threatening / Unsafe',
    signs: ['Personal threats', 'Intimidation', 'Physical aggression', 'Property damage'],
    action: 'Safety escalation — remove staff from risk · alert team · security or 000 if unsafe',
  },
];

const STEPS = [
  { icon: Heart, tone: TEAL, title: 'PAUSE & REMAIN CALM', body: 'Speak slowly. Maintain a neutral tone. Avoid confrontation.' },
  { icon: Ear, tone: TEAL, title: 'ACKNOWLEDGE', body: '"I can see this is really stressful for you." · "I understand this situation is frustrating."' },
  { icon: MessageSquare, tone: TEAL, title: 'CLARIFY & LISTEN', body: 'Allow the patient to explain. Do not interrupt. Identify the core concern.' },
  { icon: HandHeart, tone: TEAL, title: 'OFFER SOLUTION', body: 'Provide clear next steps. Offer options. Involve the radiologist if needed.' },
  { icon: Shield, tone: AMBER, title: 'SET BOUNDARIES (critical)', body: '"I want to help you, but I cannot do so while being spoken to like this. Let\'s work together to resolve this."', highlight: true },
];

const SCRIPTS = [
  { id: 'open', label: 'Opening acknowledgement', text: "I can see this is really stressful for you. Please help me understand what's happened so I can work out how to help." },
  { id: 'listen', label: 'Active listening confirmation', text: "So if I'm hearing you right, the main issue is [PARAPHRASE] — have I got that right?" },
  { id: 'solution', label: 'Offering a solution', text: "Here's what I can do right now: [OPTION]. Would that help?" },
  { id: 'boundary', label: 'Setting the boundary (Level 2)', text: "I want to help you, but I cannot do so while being spoken to like this. Let's work together to resolve this." },
  { id: 'final', label: 'Final warning before stopping care', text: "If this continues I'll need to pause your appointment and ask [SENIOR STAFF] to help. I'd prefer not to — let's settle this together." },
  { id: 'stop', label: 'Care interruption (Level 3)', text: "For the safety of everyone here I need to stop your appointment now. We will rebook / arrange alternative care — [STAFF NAME] will contact you to explain next steps." },
  { id: 'phone', label: 'Phone call de-escalation', text: "I can hear how upset you are. I want to help, but I'm finding it hard to follow while you're raising your voice. If you can share the details calmly, I can start working on this right now." },
];

const WEBSITE_STATEMENT = "We understand that medical situations can be stressful, and our team is committed to treating every patient with compassion and respect. We also maintain a zero tolerance policy toward abusive or threatening behaviour to ensure a safe environment for all patients and staff.";

const COMMON_MISTAKES = [
  { icon: Flag, title: 'Escalating too early', body: 'Damages trust. Level 1 distress is met with reassurance, not warnings.' },
  { icon: AlertTriangle, title: 'Escalating too late', body: 'Compromises safety. When threats are made, staff must be removed immediately.' },
  { icon: Ban, title: 'Not documenting', body: 'Even Level 1 events belong in RIS so patterns can be spotted.' },
];

export default function DifficultPatientsPage() {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          h1, h2, h3 { color: #0E2D5C !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="difficult-patients-page">
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate('/sops')} data-testid="dp-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button onClick={() => window.print()} data-testid="dp-print" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white" style={{ backgroundColor: TEAL }}>
            <Printer className="w-3.5 h-3.5" /> Print laminated cheat sheet
          </button>
        </div>

        {/* Cover */}
        <div className="rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: `${TEAL}33`, background: `linear-gradient(135deg, ${TEAL}0F, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${TEAL}15`, color: TEAL }}>
              <HandHeart className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${TEAL}1A`, color: TEAL }}>
              SOP · v1.0 · DIAS Std 1 + WHS Act 2011
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Distressed, Aggressive &amp; Unruly Patients</h1>
          <p className="text-sm text-muted-foreground mt-1">Compassion first · De-escalation · Clear boundaries · Safety escalation</p>
          <div className="mt-5 flex flex-wrap gap-5 text-xs">
            <Meta label="Owner" value="Medical Director" />
            <Meta label="Governance" value="Practice Manager" />
            <Meta label="Applies in" value="Reception · Procedure rooms · Phone" />
          </div>
        </div>

        {/* Core principles — stacked */}
        <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: `${TEAL}55`, backgroundColor: `${TEAL}0A` }} data-testid="dp-core-principles">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3" style={{ color: TEAL }}>Core Principles — stacked, in order</p>
          <ol className="space-y-2">
            {[
              { n: 1, tone: TEAL, text: 'All patients are treated with respect and compassion, even in distress.' },
              { n: 2, tone: AMBER, text: 'Abusive or threatening behaviour is not tolerated.' },
              { n: 3, tone: RED, text: 'Staff safety is non-negotiable.' },
            ].map(p => (
              <li key={p.n} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 shrink-0" style={{ backgroundColor: `${p.tone}20`, color: p.tone }}>{p.n}</span>
                <span className="text-sm text-foreground">{p.text}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Red-flag behaviour recognition chart — 3 levels */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${TEAL}1A`, color: TEAL }}>1</span>
          Red-flag Behaviour Recognition Chart
        </h2>
        <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="dp-levels">
          {LEVELS.map(l => (
            <div key={l.n} className="rounded-2xl border bg-card overflow-hidden" style={{ borderColor: `${l.tone}40` }}>
              <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: `${l.tone}30`, backgroundColor: `${l.tone}0A` }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: `${l.tone}20`, color: l.tone }}>{l.n}</span>
                <p className="text-sm font-bold uppercase tracking-wider" style={{ color: l.tone }}>{l.label}</p>
              </div>
              <ul className="p-4 space-y-1.5 text-sm text-foreground">
                {l.signs.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: l.tone }} /><span>{s}</span></li>
                ))}
              </ul>
              <div className="px-4 py-3 border-t text-[11px]" style={{ borderColor: `${l.tone}30` }}>
                <span className="uppercase tracking-wider font-bold text-muted-foreground">Action → </span>
                <span className="text-foreground/85">{l.action}</span>
              </div>
            </div>
          ))}
        </div>

        {/* 5-step de-escalation */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${TEAL}1A`, color: TEAL }}>2</span>
          De-escalation — 5 Steps (all staff)
        </h2>
        <div className="space-y-2 mb-8" data-testid="dp-flow">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className="flex gap-3 rounded-xl border p-4"
              style={s.highlight
                ? { borderColor: s.tone, backgroundColor: `${s.tone}0A` }
                : { borderColor: 'hsl(var(--border))' }}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold" style={{ backgroundColor: `${s.tone}15`, color: s.tone }}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <s.icon className="w-3.5 h-3.5" style={{ color: s.tone }} /> {s.title}
                </p>
                <p className="text-xs text-foreground/85 mt-1 leading-relaxed italic">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Zero-tolerance, applied proportionately */}
        <div className="rounded-2xl border p-5 mb-8" style={{ borderColor: `${AMBER}40`, backgroundColor: `${AMBER}0A` }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2" style={{ color: AMBER }}>Zero Tolerance — applied proportionately</p>
          <p className="text-sm text-foreground mb-3">Zero tolerance to abuse, threats, or violence toward staff or other patients — applied in three stages:</p>
          <ol className="space-y-1.5 text-sm text-foreground list-decimal pl-5">
            <li>Verbal warning issued.</li>
            <li>Opportunity to de-escalate provided.</li>
            <li>If behaviour continues — the appointment may be stopped, and the patient may be asked to leave.</li>
          </ol>
        </div>

        {/* Reception / staff scripts */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${TEAL}1A`, color: TEAL }}>3</span>
          Staff Scripts <span className="text-xs font-normal text-muted-foreground">(tap to copy)</span>
        </h2>
        <div className="space-y-3 mb-8" data-testid="dp-scripts">
          {SCRIPTS.map(s => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" style={{ color: TEAL }} />
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</p>
                </div>
                <button
                  onClick={() => copy(s.id, s.text)}
                  data-testid={`dp-copy-${s.id}`}
                  className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border"
                  style={copiedId === s.id
                    ? { borderColor: '#16A34A', color: '#16A34A', backgroundColor: '#16A34A10' }
                    : { borderColor: `${TEAL}40`, color: TEAL }}
                >
                  {copiedId === s.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="text-sm text-foreground leading-relaxed italic">"{s.text}"</p>
            </div>
          ))}
        </div>

        {/* Continuity of care + special considerations */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><HandHeart className="w-3.5 h-3.5" /> Continuity of care</p>
            <p className="text-sm text-foreground mb-2">If care is interrupted, the patient must be advised of:</p>
            <ul className="space-y-1 text-sm text-foreground list-disc pl-5">
              <li>Why the procedure cannot continue.</li>
              <li>Next steps for care.</li>
              <li>Alternative arrangements where appropriate.</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Special considerations</p>
            <p className="text-sm text-foreground"><strong>Vulnerable patients</strong> — elderly, cognitive impairment, mental health: extra care; do not apply zero-tolerance boundaries the same way.</p>
            <p className="text-sm text-foreground mt-2"><strong>Cultural sensitivity</strong> — use interpreters where needed; avoid assumptions.</p>
          </div>
        </div>

        {/* Website statement */}
        <div className="rounded-xl border border-border bg-card p-5 mb-8" data-testid="dp-website-statement">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Website statement (patient-facing)</p>
            <button
              onClick={() => copy('web', WEBSITE_STATEMENT)}
              data-testid="dp-copy-website"
              className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border"
              style={copiedId === 'web'
                ? { borderColor: '#16A34A', color: '#16A34A', backgroundColor: '#16A34A10' }
                : { borderColor: `${TEAL}40`, color: TEAL }}
            >
              {copiedId === 'web' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          </div>
          <p className="text-sm text-foreground leading-relaxed font-mono bg-muted/60 rounded-md p-3">{WEBSITE_STATEMENT}</p>
        </div>

        {/* Common mistakes */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${RED}15`, color: RED }}>4</span>
          Biggest Real-World Mistakes
        </h2>
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {COMMON_MISTAKES.map((m, i) => (
            <div key={i} className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <m.icon className="w-4 h-4 text-red-600" />
                <p className="text-sm font-bold text-foreground">{m.title}</p>
              </div>
              <p className="text-xs text-foreground/85 leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>

        {/* Documentation + incident reporting */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Documentation (mandatory)</p>
            <ul className="space-y-2 text-sm text-foreground">
              {['Behaviour observed', 'Staff involved', 'Actions taken', 'Warnings given', 'Outcome'].map(c => (
                <li key={c} className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" /><span>{c}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Incident report mandatory if</p>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-red-500" />Threats made</li>
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-red-500" />Procedure interrupted</li>
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-red-500" />Staff safety compromised</li>
            </ul>
          </div>
        </div>

        {/* Staff safety measures */}
        <div className="rounded-xl border border-border bg-card p-5 mb-8">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Staff safety measures</p>
          <ul className="grid sm:grid-cols-3 gap-3 text-sm text-foreground">
            <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: TEAL }} />Never remain alone in unsafe situations.</li>
            <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: TEAL }} />Know panic / escalation protocols.</li>
            <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: TEAL }} />Keep exit pathways clear at all times.</li>
          </ul>
        </div>

        <p className="no-print text-center text-[11px] text-muted-foreground mb-2">
          Owner: Medical Director · Governance: Practice Manager · Review: Annual
        </p>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">{label}</span>
      <div className="text-foreground font-semibold mt-0.5">{value}</div>
    </div>
  );
}
